<template>
  <div
    class="chart">
    <vue-c3
      :handler="handler"/>
  </div>
</template>

<script>
import Vue from 'vue';
import VueC3 from 'vue-c3';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import localeData from 'dayjs/plugin/localeData';
import isoWeek from 'dayjs/plugin/isoWeek';

require('c3/c3.css');

export default {
  name: 'HealthChart',
  components: {
    VueC3
  },
  props: {
    data: {
      type: Object,
      default: () => {
      }
    },
    xAxisFormat: {
      type: String,
      default: '%Y-%m-%d %H:%M'
    }
  },
  data () {
    return {
      handler: new Vue(),
      glucose: 'orange',
      pulse: 'black',
      carbohydrates: '#C4CC52',
      proteins: '#9765E0',
      fats: '#00C2BA',
      redGluco: '#e73434',
      orangeGluco: '#dc7205',
      greenGluco: '#2a940c',
      sport: '#bd70ff',
      event: '#4bd3de',
      questionnaireMorning: '#6e4a23',
      questionnaireNight: '#6600ad',
      food_proteins: 0,
      food_carbohydrates: 0,
      food_fats: 0,
      food_calories: 0,
      food_amount: 0,
      questMorningHour: 9,
      questNightHour: 21,
      glucoscoreColors: [
        '#999999',
        '#FC3731',
        '#FC3731',
        '#FD722B',
        '#FD722B',
        '#FE8D00',
        '#FFC500',
        '#50BA00',
        '#50BA00',
        '#28C18C',
        '#28C18C'
      ]
    };
  },
  watch: {
    data: {
      handler () {
        this.draw();
      },
      deep: true
    }
  },
  created () {
    dayjs.extend(localizedFormat);
    dayjs.extend(localeData);
    dayjs.localeData();
    dayjs.extend(isoWeek);
  },
  mounted () {
    this.draw();
  },
  methods: {
    getFoodMenuByTimesptam (timestamp) {
      const food = this.data.food.find(food => food.timestamp === new Date(timestamp).getTime());
      this.food_proteins = 0;
      this.food_carbohydrates = 0;
      this.food_fats = 0;
      this.food_calories = 0;
      this.food_amount = 0;
      let re = '';
      if (food.type) {
        re += `
              <tr>
              <th >` + food.type + `</th>
              <th colspan="100%"/>
              </tr>`;
      }
      if (food.note) {
        re += `
              <tr>
              <td colspan="100%">` + food.note + `</td>
              </tr>`;
      }
      if (food.glucoscore) {
        const score = food.glucoscore.score;
        let color = this.glucoscoreColors[0];
        if (score != null) {
          color = this.glucoscoreColors[score];
          const aucDecimated = food.glucoscore.auc_decimated;
          const peak = food.glucoscore.glucose_max_decimated;
          const stabilization = food.glucoscore.stabilization_decimated;
          const amplitude = food.glucoscore.amplitude_decimated;

          const aucColor = this.glucoscoreColors[Math.floor(food.glucoscore.auc_decimated)];
          const peakColor = this.glucoscoreColors[Math.floor(food.glucoscore.glucose_max_decimated)];
          const stabilizationColor = this.glucoscoreColors[Math.floor(food.glucoscore.stabilization_decimated)];
          const amplitudeColor = this.glucoscoreColors[Math.floor(food.glucoscore.amplitude_decimated)];
          re += `
              <tr>
              <th >` + 'Glucoscore' + `</th>
              <th >` + this.$t('health.explorer.glucoscore.auc') + `</th>
              <th >` + this.$t('health.explorer.glucoscore.peak') + `</th>
              <th >` + this.$t('health.explorer.glucoscore.stabilization') + `</th>
              <th >` + this.$t('health.explorer.glucoscore.amplitude') + `</th>
              <th colspan="100%"/>
              </tr>`;
          re += `
              <tr>
              <td style="text-align: right; color: white; background-color:` + color + `">` + score + `</td>
              <td style="text-align: right; color: white; background-color:` + aucColor + `">` + aucDecimated + `</td>
              <td style="text-align: right; color: white; background-color:` + peakColor + `">` + peak + `</td>
              <td style="text-align: right; color: white; background-color:` + stabilizationColor + `">` + stabilization + `</td>
              <td style="text-align: right; color: white; background-color:` + amplitudeColor + `">` + amplitude + `</td>
              <td style="background: #B3B3B3"></td>
              </tr>`;
        } else {
          const message = this.getGlucoscoreNotCalculatedText(food.glucoscore, food.timestamp);
          re += `
              <tr>
              <th colspan="100%">` + 'Glucoscore' + `</th>
              </tr>`;
          re += `
              <tr>
              <td colspan="100%">` + message + `</td>
              </tr>`;
        }
      }

      re += `<tr>
                <th>` + this.$t('health.explorer.foods') + `</th>
                <th>` + this.$t('health.explorer.amount') + `</th>
                <th style="background-color:` + this.carbohydrates + `;" >` + this.$t('health.explorer.carbohydrates') + `</th>
                <th style="background-color:` + this.proteins + `;">` + this.$t('health.explorer.proteins') + `</th>
                <th style="background-color:` + this.fats + `;">` + this.$t('health.explorer.fats') + `</th>
                <th >Kcals</th>
              </tr> `;

      food.meal_items.forEach(mealItem => {
        this.food_calories += parseFloat(mealItem.calories);
        this.food_fats += parseFloat(mealItem.fat);
        this.food_carbohydrates += parseFloat(mealItem.carbohydrate);
        this.food_proteins += parseFloat(mealItem.protein);
        this.food_amount += parseFloat(mealItem.amount);

        re += `<tr>
                <td style="text-align: right;">` + mealItem.food_name + `</td>
                <td style="text-align: right;">` + this.round(mealItem.amount) + `</td>
                <td style="text-align: right;">` + this.round(mealItem.carbohydrate) + `</td>
                <td style="text-align: right;">` + this.round(mealItem.protein) + `</td>
                <td style="text-align: right;">` + this.round(mealItem.fat) + `</td>
                <td style="text-align: right;">` + this.round(mealItem.calories) + `</td>
              </tr>`;
      });
      return re;
    },
    getGlucoscoreNotCalculatedText (glucoscore, timestamp) {
      if (glucoscore == null) {
        return this.$t('health.explorer.glucoscore.notCalculated');
      }
      if (glucoscore.score != null) {
        return '';
      }
      const couldNotBeCalculatedYet = new Date().getTime() - timestamp > 7200000;
      if (glucoscore.no_samples) {
        // Check if 2 hours have passed since meal
        if (couldNotBeCalculatedYet) {
          return this.$t('health.explorer.glucoscore.couldNotCalculateYet');
        }
        return this.$t('health.explorer.glucoscore.notSamples');
      } else if (glucoscore.little_samples) {
        // Check if 2 hours have passed since meal
        if (couldNotBeCalculatedYet) {
          return this.$t('health.explorer.glucoscore.couldNotCalculateYet');
        }
        return this.$t('health.explorer.glucoscore.notEnoughSamples');
      } else if (glucoscore.during_sport) {
        return this.$t('health.explorer.glucoscore.duringSport');
      } else if (glucoscore.before_sport) {
        return this.$t('health.explorer.glucoscore.beforeSport');
      } else if (glucoscore.too_close_to_previous_meal) {
        return this.$t('health.explorer.glucoscore.tooCloseToMeal');
      }
      return this.$t('health.explorer.glucoscore.notCalculated');
    },
    getSportByTimeStamp (timestamp) {
      const sport = this.data.sport.find(sport => sport.timestamp === new Date(timestamp).getTime());
      return sport;
    },
    getEventByTimeStamp (timestamp) {
      const event = this.data.event.find(event => event.timestamp === new Date(timestamp).getTime());
      return event;
    },
    round (value) {
      return (Math.round(value * 100) / 100);
    },
    getPopupData (data) {
      let popup = '';
      let foodPrinted = false;
      let foodTotalPrinted = false;

      data.forEach((dataValue, index) => {
        popup += '<tr>';
        if (dataValue !== null && dataValue.name !== null && dataValue.value !== null) {
          if (dataValue.name === 'Glucose' || dataValue.name === 'Pulse') {
            popup += '<td class=\'name\' style="display: flex">';
            popup += '<span style="width: 0.7rem; height: 0.7rem; background-color: ' + this.getPopupColor(dataValue.name) + ';"></span>';
            popup += dataValue.id;
            popup += '</td>';
            popup += '<td class=\'value\'>';
            popup += dataValue.value;
            popup += '</td>';
          } else if (dataValue.name === 'Carbohydrates' && dataValue.value !== 0 && !foodPrinted) {
            popup += this.getFoodMenuByTimesptam(dataValue.x);
            foodPrinted = true;
          } else if (dataValue.name === 'Fats' && dataValue.value !== 0 && !foodPrinted) {
            popup += this.getFoodMenuByTimesptam(dataValue.x);
            foodPrinted = true;
          } else if (dataValue.name === 'Calories' && dataValue.value !== 0 && !foodPrinted) {
            popup += this.getFoodMenuByTimesptam(dataValue.x);
            foodPrinted = true;
          } else if (dataValue.name === 'Proteins' && dataValue.value !== 0 && !foodPrinted) {
            popup += this.getFoodMenuByTimesptam(dataValue.x);
            foodPrinted = true;
          } else if (dataValue.name === 'Sport' && dataValue.value !== 0 && !foodTotalPrinted) {
            let sport = this.getSportByTimeStamp(dataValue.x);
            if (sport.note !== null && sport.note !== '') {
              popup += `
              <tr>
              <td colspan="100%" style="max-width: 500px;">` + sport.note + `</td>
              </tr>`;
            }
            popup += '<td class=\'name\' style="display: flex">';
            popup += this.$t('health.xugarboard.duration');
            popup += '</td>';

            popup += '<td class=\'value\'>';
            const seconds = Math.floor(sport.duration / 1000);
            const durationMinutes = seconds / 60;
            popup += this.getTimeText(durationMinutes);
            popup += '</td>';
          } else if (dataValue.name === 'Event' && dataValue.value !== 0 && !foodTotalPrinted) {
            const event = this.getEventByTimeStamp(dataValue.x);
            if (event.note !== null && event.note !== '') {
              popup += `
              <tr>
              <td colspan="100%" style="max-width: 500px;">` + event.note + `</td>
              </tr>`;
            }
            popup += '<td class=\'name\' style="display: flex">';
            popup += this.$t('health.xugarboard.duration');
            popup += '</td>';
            popup += '<td class=\'value\'>';
            const seconds = Math.floor(event.duration / 1000);
            const durationMinutes = seconds / 60;
            popup += this.getTimeText(durationMinutes);
            popup += '</td>';
          } else if (dataValue.name === 'Morning questionnaire' && dataValue.value !== 0 && !foodTotalPrinted) {
            const questionnaire = this.getQuestionnaireByTimeStamp(dataValue.x, this.data.questionnaireMorning, this.questMorningHour);
            popup += questionnaire ? this.getQuestionnaireMorningPopup(questionnaire) : '';
          } else if (dataValue.name === 'Night questionnaire' && dataValue.value !== 0 && !foodTotalPrinted) {
            const questionnaire = this.getQuestionnaireByTimeStamp(dataValue.x, this.data.questionnaireNight, this.questNightHour);
            popup += questionnaire ? this.getQuestionnaireNightPopup(questionnaire) : '';
          }
        }
        popup += '</tr>';
        if (foodPrinted && !foodTotalPrinted) {
          foodTotalPrinted = true;
          let row = `<tr>
                <td style="text-align: right;font-weight: bold;">TOTAL</td>
                <td style="text-align: right;font-weight: bold;">` + this.round(this.food_amount) + ` gr</td>
                <td style="text-align: right;font-weight: bold;">` + this.round(this.food_carbohydrates) + ` gr</td>
                <td style="text-align: right;font-weight: bold;">` + this.round(this.food_proteins) + ` gr</td>
                <td style="text-align: right;font-weight: bold;">` + this.round(this.food_fats) + ` gr</td>
                <td style="text-align: right;font-weight: bold;">` + this.round(this.food_calories) + ` Kcals</td>
              </tr>`;
          popup += row;
        }
      });
      return popup;
    },
    getPopupColor (dataValue) {
      const color = dataValue.toLowerCase();
      return this[color];
    },
    draw () {
      if (this.data === null || this.data === {}) {
        return;
      }
      const context = this;
      const glucoseDates = this.getGlucoseDates();
      const glucoseValues = this.getGlucoseValues();
      const heartRateDates = this.getHeartRateDates();
      const heartRateValues = this.getHeartRateValues();

      const foodDates = this.getFoodDates();
      const foodValues = this.getFoodValues();

      const sportDates = this.getSportDates();
      const sportValues = this.getSportValues();
      const sport = this.getSport();

      const eventDates = this.getEventDates();
      const eventValues = this.getEventValues();
      const event = this.getEvent();
      const sportAndEvents = sport.concat(event);

      const questionnaireMorningDates = this.getQuestionnaireMorningDates();
      const questionnaireMorningValues = this.getQuestionnaireMorningValues();

      const questionnaireNightDates = this.getQuestionnaireNightDates();
      const questionnaireNightValues = this.getQuestionnaireNightValues();

      const options = {
        data: {
          xs: {
            'Glucose': 'GlucoseDates',
            'Pulse': 'PulseDates',
            'Carbohydrates': 'FoodDates',
            'Proteins': 'FoodDates',
            'Fats': 'FoodDates',
            'Sport': 'SportDates',
            'Event': 'EventDates',
            'Morning questionnaire': 'QuestionnaireMorningDates',
            'Night questionnaire': 'QuestionnaireNightDates'
          },
          xFormat: '%Y-%m-%d %H:%M',
          columns: [
            ['GlucoseDates', ...glucoseDates],
            ['Glucose', ...glucoseValues],
            ['PulseDates', ...heartRateDates],
            ['Pulse', ...heartRateValues],
            ['FoodDates', ...foodDates],
            ['Carbohydrates', ...foodValues.carbohydrates],
            ['Proteins', ...foodValues.proteins],
            ['Fats', ...foodValues.fats],
            ['SportDates', ...sportDates],
            ['Sport', ...sportValues],
            ['EventDates', ...eventDates],
            ['Event', ...eventValues],
            ['QuestionnaireMorningDates', ...questionnaireMorningDates],
            ['Morning questionnaire', ...questionnaireMorningValues],
            ['QuestionnaireNightDates', ...questionnaireNightDates],
            ['Night questionnaire', ...questionnaireNightValues]
            // ['IG_medium', 3, 0, 0, 0, 1, 0, 1, 0, 0, 0, 0, 0, 0, 1],
            // ['IG_low', 2, 0, 0, 0, 0, 0, 4, 0, 3.5, 0, 0, 0, 0, 6],
            // ['IG_high', 0, 0, 0, 0, 2, 0, 0, 0, 0, 0, 0, 0, 0, 0]
          ],
          type: 'bar',
          colors: {
            Glucose: this.glucose,
            Pulse: this.pulse,
            Carbohydrates: this.carbohydrates,
            Proteins: this.proteins,
            Fats: this.fats,
            Sport: this.sport,
            Event: this.event,
            'Morning questionnaire': this.questionnaireMorning,
            'Night questionnaire': this.questionnaireNight
          },
          types: {
            Glucose: 'line',
            Pulse: 'line',
            Sport: 'area-step',
            Event: 'area-step'
          },
          groups: [
            ['Carbohydrates', 'Proteins', 'Fats']
          ],
          order: 'asc',
          axes: {
            Glucose: 'y',
            Pulse: 'y',
            Carbohydrates: 'y2',
            Proteins: 'y2',
            Fats: 'y2'
          }
        },
        zoom: {
          /* Caution with the performance */
          enabled: false
        },
        bar: {
          width: {
            ratio: 0.1 // this makes bar width 50% of length between ticks
          }
          // or
          // width: 100 // this makes bar width 100px
        },

        line: {
          connectNull: true,
          step: {
            type: 'step-after'
          }
        },
        tooltip: {
          contents: function (d) {
            return `<div class='c3-tooltip-container custom-tooltip'>
                     <table align="right" class='c3-tooltip'>
                     <tbody><tr>
                     <th colspan='6'>` + dayjs(d[0].x.toString()).format('YYYY-MM-DD HH:mm') +
              `</th></tr>` + context.getPopupData(d) +
              `</tbody></table></div> `;
          }
        },
        grid: {
          x: {
            lines: sportAndEvents
          }
        },
        axis: {
          x: {
            type: 'timeseries',
            // if true, treat x value as localtime (Default)
            // if false, convert to UTC internally
            tick: {
              fit: false,
              format: this.xAxisFormat
              // culling: {
              //   max: 4 // the number of tick texts will be adjusted to less than this value
              // }
            },
            label: 'Timestamp'
          },
          y2: {
            max: undefined,
            show: true,
            label: 'Gramos',
            padding: {
              top: 500
            }
          },
          y: {
            min: 20,
            label: 'Glucosa (mg/dl) - Pulso (bpm)'
          }
        }

      };

      this.handler.$emit('init', options);
    },
    getDates (timestamps) {
      return timestamps.map(timestamp => new Date(timestamp));
    },
    getGlucoseDates () {
      const timestamps = [];
      this.data.glucose.forEach(glucose => timestamps.push(new Date(glucose.timestamp)));
      return timestamps;
    },
    getGlucoseValues () {
      const glucoseValue = [];
      this.data.glucose.forEach((glucose) => {
        glucoseValue.push(glucose.value);
      });
      return glucoseValue;
    },
    getHeartRateDates () {
      const timestamps = [];
      this.data.heart_rate.forEach(heartRate => timestamps.push(new Date(heartRate.timestamp)));
      return timestamps;
    },
    getHeartRateValues () {
      const hearRateValue = [];
      this.data.heart_rate.forEach((heartRate) => {
        hearRateValue.push(heartRate.value);
      });
      return hearRateValue;
    },
    getFoodDates () {
      const timestamps = [];
      this.data.food.forEach(food => timestamps.push(new Date(food.timestamp)));
      return timestamps;
    },
    getFoodValues () {
      const carbohydrates = [];
      const proteins = [];
      const fats = [];
      this.data.food.forEach((food) => {
        let carbohydrate = 0;
        let protein = 0;
        let fat = 0;
        if (food) {
          for (let i = 0; i < food.meal_items.length; i++) {
            carbohydrate += parseFloat(food.meal_items[i].carbohydrate.toString());
            protein += parseFloat(food.meal_items[i].protein.toString());
            fat += parseFloat(food.meal_items[i].fat.toString());
          }
        }
        carbohydrates.push(Math.round(carbohydrate * 100) / 100);
        proteins.push(Math.round(protein * 100) / 100);
        fats.push(Math.round(fat * 100) / 100);
      });
      return {
        carbohydrates: carbohydrates,
        proteins: proteins,
        fats: fats,
        type: this.data.food.type,
        note: this.data.food.note
      };
    },
    getSportDates () {
      const timestamps = [];
      this.data.sport.map(sport => {
        const betweenSportsEndingAfter = this.data.sport
          .filter(sport1 => sport1.timestamp > sport.timestamp && sport1.timestamp < (sport.timestamp + sport.duration) && (sport1.timestamp + sport1.duration) > (sport.timestamp + sport.duration))
          .sort((a, b) => a.timestamp - b.timestamp);
        let end = sport.timestamp + sport.duration;
        if (betweenSportsEndingAfter.length > 0) {
          end = betweenSportsEndingAfter[0].timestamp - 1000;
        }
        timestamps.push(new Date(sport.timestamp));
        timestamps.push(new Date(end));
      });
      return timestamps;
    },
    getSportValues () {
      const sportValues = [];
      this.data.sport.forEach(() => {
        sportValues.push(200);
        sportValues.push(0);
      });
      return sportValues;
    },
    getSport () {
      return this.data.sport.map(sport => {
        const intensityText = sport.intensity ? ' - ' + this.$t('health.explorer.intensity') + ':  ' + sport.intensity : '';
        return {
          start: new Date(sport.timestamp),
          end: new Date(sport.timestamp + sport.duration),
          opacity: 0.5,
          class: 'region' + sport.intensity,
          // label: sport.type + ' ' +this.$t('health.explorer.intensity')  + ':  ' + sport.intensity,
          value: new Date(sport.timestamp),
          text: this.$t('health.explorer.sport') + ': ' + sport.type + intensityText,
          position: 'end'
        };
      });
    },
    getEventDates () {
      const timestamps = [];
      this.data.event.map(event => {
        const betweenEventsEndingAfter = this.data.event
          .filter(event1 => event1.timestamp > event.timestamp && event1.timestamp < (event.timestamp + event.duration) && (event1.timestamp + event1.duration) > (event.timestamp + event.duration))
          .sort((a, b) => a.timestamp - b.timestamp);
        let end = event.timestamp + event.duration;
        if (betweenEventsEndingAfter.length > 0) {
          end = betweenEventsEndingAfter[0].timestamp - 1000;
        }
        timestamps.push(new Date(event.timestamp));
        timestamps.push(new Date(end));
      });
      return timestamps;
    },
    getEventValues () {
      const eventValues = [];
      this.data.event.forEach(() => {
        eventValues.push(100);
        eventValues.push(0);
      });
      return eventValues;
    },
    getEvent () {
      return this.data.event.map(event => {
        const intensityText = event.intensity ? ' - ' + this.$t('health.explorer.intensity') + ':  ' + event.intensity : '';
        return {
          start: new Date(event.timestamp),
          end: new Date(event.timestamp + event.duration),
          opacity: 0.5,
          value: new Date(event.timestamp),
          text: this.$t('health.explorer.event') + ': ' + event.type + intensityText,
          position: 'middle'
        };
      });
    },
    getQuestionnaireMorningDates () {
      const timestamps = [];
      this.data.questionnaireMorning.forEach(questionnaire => {
        const questDate = new Date(questionnaire.timestamp * 1000);
        questDate.setHours(this.questMorningHour, 0, 0);
        timestamps.push(questDate);
      });
      return timestamps;
    },
    getQuestionnaireMorningValues () {
      const questionnaireValues = [];
      this.data.questionnaireMorning.forEach(() => {
        questionnaireValues.push(70);
      });
      return questionnaireValues;
    },
    getQuestionnaireNightDates () {
      const timestamps = [];
      this.data.questionnaireNight.forEach(questionnaire => {
        const questDate = new Date(questionnaire.timestamp * 1000);
        questDate.setHours(this.questNightHour, 0, 0);
        timestamps.push(questDate);
      });
      return timestamps;
    },
    getQuestionnaireNightValues () {
      const questionnaireValues = [];
      this.data.questionnaireNight.forEach(() => {
        questionnaireValues.push(70);
      });
      return questionnaireValues;
    },
    getQuestionnaireByTimeStamp (timestamp, questionnaries, hour) {
      const questionnaire = questionnaries.find(questionnaire => {
        const questDate = new Date(questionnaire.timestamp * 1000);
        questDate.setHours(hour, 0, 0);
        return questDate.getTime() === new Date(timestamp).getTime();
      });
      return questionnaire;
    },
    getQuestionnaireMorningPopup (questionnaire) {
      let popup = '';
      if (questionnaire.note) {
        popup += `
              <tr>
              <td class='name' style="display: flex">` + this.$t('health.explorer.questionnaire.notes') + `</td>
              <td style="max-width: 500px">` + questionnaire.note + `</td>
              </tr>`;
      }
      popup += '<td class=\'name\' style="display: flex">';
      popup += this.$t('health.explorer.questionnaire.fastingHunger');
      popup += '</td> <td class=\'value\'>';
      popup += questionnaire.fastingHunger;
      popup += '</td></tr><tr>';
      popup += '<td class=\'name\' style="display: flex">';
      popup += this.$t('health.explorer.questionnaire.glucoseBasal');
      popup += '</td> <td class=\'value\'>';
      popup += questionnaire.glucoseBasal;
      popup += '</td></tr><tr>';
      popup += '<td class=\'name\' style="display: flex">';
      popup += this.$t('health.explorer.questionnaire.restingHr');
      popup += '</td> <td class=\'value\'>';
      popup += questionnaire.restingHr;
      popup += '</td></tr><tr>';
      popup += '<td class=\'name\' style="display: flex">';
      popup += this.$t('health.explorer.questionnaire.hrVariability');
      popup += '</td> <td class=\'value\'>';
      popup += questionnaire.hrVariability;
      popup += '</td></tr><tr>';
      popup += '<td class=\'name\' style="display: flex">';
      popup += this.$t('health.explorer.questionnaire.sickness');
      popup += '</td> <td class=\'value\'>';
      popup += questionnaire.sickness ? this.$t('health.explorer.questionnaire.yes') : this.$t('health.explorer.questionnaire.no');
      popup += '</td></tr><tr>';
      popup += '<td class=\'name\' style="display: flex">';
      popup += this.$t('health.explorer.questionnaire.sleepQuality');
      popup += '</td> <td class=\'value\'>';
      popup += questionnaire.sleepQuality;
      popup += '</td></tr><tr>';
      popup += '<td class=\'name\' style="display: flex">';
      popup += this.$t('health.explorer.questionnaire.sleepTime');
      popup += '</td> <td class=\'value\'>';
      popup += this.getTimeText(questionnaire.sleepTime);
      popup += '</td></tr><tr>';
      popup += '<td class=\'name\' style="display: flex">';
      popup += this.$t('health.explorer.questionnaire.tiredness');
      popup += '</td> <td class=\'value\'>';
      popup += questionnaire.tiredness;
      popup += '</td></tr>';

      if (questionnaire.sleepStartTimestamp != null) {
        const sleepStartTimestamp = new Date(questionnaire.sleepStartTimestamp);
        let hoursStart = sleepStartTimestamp.getHours();
        let minutesStart = sleepStartTimestamp.getMinutes();
        if (minutesStart < 10) {
          minutesStart = '0' + minutesStart;
        }
        const sleepStartValue = hoursStart + ':' + minutesStart;
        popup += '<tr><td class=\'name\' style="display: flex">';
        popup += this.$t('health.explorer.questionnaire.sleepTimeStart');
        popup += '</td> <td class=\'value\'>';
        popup += sleepStartValue;
        popup += '</td></tr>';
      }

      if (questionnaire.sleepEndTimestamp != null) {
        const sleepEndTimestamp = new Date(questionnaire.sleepEndTimestamp);
        let hoursEnd = sleepEndTimestamp.getHours();
        let minutesEnd = sleepEndTimestamp.getMinutes();
        if (minutesEnd < 10) {
          minutesEnd = '0' + minutesEnd;
        }
        const sleepEndValue = hoursEnd + ':' + minutesEnd;

        popup += '<tr><td class=\'name\' style="display: flex">';
        popup += this.$t('health.explorer.questionnaire.sleepTimeEnd');
        popup += '</td> <td class=\'value\'>';
        popup += sleepEndValue;
        popup += '</td></tr>';
      }
      return popup;
    },
    getQuestionnaireNightPopup (questionnaire) {
      let popup = '';
      if (questionnaire.note) {
        popup += `
              <tr>
              <td class='name' style="display: flex">` + this.$t('health.explorer.questionnaire.notes') + `</td>
              <td style="max-width: 500px">` + questionnaire.note + `</td>
              </tr>`;
      }
      popup += '<td class=\'name\' style="display: flex">';
      popup += this.$t('health.explorer.questionnaire.anxietyLevel');
      popup += '</td> <td class=\'value\'>';
      popup += questionnaire.anxietylevel;
      popup += '</td></tr><tr>';
      popup += '<td class=\'name\' style="display: flex">';
      popup += this.$t('health.explorer.questionnaire.dayEvaluation');
      popup += '</td> <td class=\'value\'>';
      popup += questionnaire.dayEvaluation;
      popup += '</td></tr><tr>';
      popup += '<td class=\'name\' style="display: flex">';
      popup += this.$t('health.explorer.questionnaire.nutritionPlan');
      popup += '</td> <td class=\'value\'>';
      popup += questionnaire.nutritionPlan > 0 ? this.$t('health.explorer.questionnaire.yes') : this.$t('health.explorer.questionnaire.no');
      popup += '</td></tr><tr>';
      popup += '<td class=\'name\' style="display: flex">';
      popup += this.$t('health.explorer.questionnaire.outOfRoutine');
      popup += '</td> <td class=\'value\'>';
      popup += questionnaire.outOfRoutine ? this.$t('health.explorer.questionnaire.yes') : this.$t('health.explorer.questionnaire.no');
      popup += '</td></tr><tr>';
      popup += '<td class=\'name\' style="display: flex">';
      popup += this.$t('health.explorer.questionnaire.trainingEffort');
      popup += '</td> <td class=\'value\'>';
      popup += questionnaire.trainingEffort;
      popup += '</td>';

      return popup;
    },
    getTimeText (timeInMinutes) {
      let text = '';
      if (timeInMinutes / 60 >= 1) {
        const durationHours = (timeInMinutes - (timeInMinutes % 60)) / 60;
        timeInMinutes = timeInMinutes % 60;
        text += durationHours + 'h ';
        if (timeInMinutes > 0) {
          text += timeInMinutes + 'min';
        }
      } else {
        text += timeInMinutes + 'min';
      }
      return text;
    }

  }
}
;
</script>

<style lang="scss" scoped>
@import "../../../../stylesheets/variables.scss";
</style>



// WEBPACK FOOTER //
// src/brand/health/components/explorer/HealthChart.vue